import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { IEditDrawerComponent } from 'src/app/lego-utils';
import { NzDrawerRef } from 'ng-zorro-antd/drawer';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { finalize, map, switchMap, tap } from 'rxjs/operators';
import { generateEcoErrorMessage } from 'src/app/components/main-content/design-lookup/material-bom/generate-eco-error-message';
import { BOMItem } from 'src/app/models';
import { TeamcenterService } from 'src/app/services';
import {
  BOMHeaderService,
  BOMItemService,
  DesignService,
  MaterialSearchService,
} from 'src/app/state';
import { MaterialBomFormService } from '../material-bom-form.service';

@Component({
  selector: 'em-create-edit-material-bom',
  styleUrls: ['./create-edit-material-bom.drawer.less'],
  templateUrl: './create-edit-material-bom.drawer.html',
  standalone: false,
})
export class CreateEditMaterialBomDrawer
  implements IEditDrawerComponent<BOMItem>, OnDestroy, OnInit
{
  public isLoading = false;
  public submitError?: string;
  public bomForm?: UntypedFormGroup;
  public isEdit = false;
  public isBomLocked = false;
  public loadingMessage = '';
  public readonly isBomStatusUnavailable$ = combineLatest([
    this.bomHeaderService.isBomLocked$,
    this.bomHeaderService.hasLoaded$,
  ]).pipe(map(([isBomLocked, hasLoaded]) => hasLoaded && isBomLocked === undefined));

  private readonly subscriptions = new Subscription();

  public constructor(
    private readonly drawerRef: NzDrawerRef<boolean>,
    private readonly bomItemService: BOMItemService,
    private readonly materialBomFormService: MaterialBomFormService,
    private readonly teamcenterService: TeamcenterService,
    private readonly designService: DesignService,
    private readonly bomHeaderService: BOMHeaderService,
    private readonly notification: NzNotificationService,
    public readonly materialSearchService: MaterialSearchService
  ) {}

  @Input() public set editValue(bomItem: BOMItem) {
    this.subscriptions.add(
      this.bomHeaderService.isBomLocked$.subscribe((isBomLocked) => {
        if (isBomLocked === undefined) {
          this.bomForm = undefined;

          return;
        }

        this.isBomLocked = isBomLocked;
        this.bomForm = this.materialBomFormService.createFormGroup(this.isBomLocked, bomItem);
      })
    );

    this.isEdit = true;
  }

  public ngOnInit(): void {
    // check if it is the edit option, if it is edit, we do nothing, as the editValue is called before the ngOnInit
    if (this.isEdit) {
      return;
    }

    this.subscriptions.add(
      this.bomHeaderService.isBomLocked$.subscribe((isBomLocked) => {
        if (isBomLocked === undefined) {
          this.bomForm = undefined;

          return;
        }

        this.isBomLocked = isBomLocked;
        this.bomForm = this.materialBomFormService.createFormGroup(isBomLocked);
      })
    );
  }

  public submitForm(formGroup: UntypedFormGroup | undefined): void {
    if (!formGroup) {
      throw new Error('Trying to save but there was no data');
    }

    const designId = this.designService.getDesignId();

    if (!designId) {
      throw new Error('Trying to save but there was no designId');
    }

    this.submitError = undefined;
    this.loadingMessage = 'Saving...';
    this.isLoading = true;

    const entity = this.materialBomFormService.adaptToDomain(formGroup);

    const submit$: Observable<BOMItem> = this.isEdit
      ? this.bomItemService.update(entity.id ?? '', entity)
      : this.bomItemService.add(entity);

    this.subscriptions.add(
      submit$
        .pipe(
          tap({
            error: (error) => {
              const sapErrorMessage = error instanceof Error ? error.message : 'Unknown error';

              this.submitError = generateEcoErrorMessage(sapErrorMessage, this.isBomLocked);
            },
            next: () => {
              this.loadingMessage = 'Syncing SAP with Teamcenter...';
            },
          }),
          switchMap(() =>
            this.teamcenterService.startSync(designId).pipe(
              tap({
                error: (error: unknown) => {
                  this.notification.error(
                    'Error',
                    `Your changes were saved, but syncing SAP and Teamcenter failed. Syncing happens automatically every 15 minutes. Error message: ${
                      error instanceof Error ? error.message.split('§§§')[0] : 'Unknown error'
                    }`,
                    { nzDuration: 0 }
                  );

                  this.drawerRef.close(true);
                },
              })
            )
          ),
          finalize(() => {
            this.isLoading = false;
          })
        )
        .subscribe({
          next: () => {
            this.drawerRef.close(true);
          },
        })
    );
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
